import { makeStyles } from '@material-ui/core';

export const useSelectorStyles = makeStyles((theme) => ({
  selectorContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  propertyFilterContainer: {
    minWidth: 500
  },
  container: {
    position: 'relative',
    minWidth: 360,
    [theme.breakpoints.down('sm')]: {
      minWidth: 320,
    },
  },
  textField: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e0e0e0',
      transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], {
        duration: theme.transitions.duration.short,
      }),
      '& fieldset': {
        borderColor: 'transparent',
        borderWidth: 0,
      },
      '&:hover': {
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderColor: theme.palette.primary.light,
        '& fieldset': {
          borderColor: 'transparent',
        },
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        borderColor: theme.palette.primary.main,
        '& fieldset': {
          borderColor: 'transparent',
        },
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: '1.125rem',
      '&.Mui-focused': {
        color: theme.palette.primary.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '1.125rem',
      fontWeight: theme.typography.fontWeightRegular,
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.secondary,
        fontSize: '1rem',
        fontWeight: theme.typography.fontWeightRegular,
      },
    },
  },
  inputAdornment: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1.5),
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  languageIcon: {
    fontSize: 20,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  helpIcon: {
    marginLeft: theme.spacing(1),
    fontSize: '1.2em',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  searchIcon: {
    fontSize: 18,
    color: theme.palette.text.secondary,
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  statusChip: {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: theme.typography.fontWeightMedium,
    marginLeft: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 2,
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: theme.shadows[2],
    },
  },
  activeChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
  },
  maintenanceChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    '&:hover': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
  },
  inactiveChip: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.grey[800],
    },
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: theme.zIndex.modal,
    maxHeight: 360,
    overflow: 'hidden',
    marginTop: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #e0e0e0',
    animation: '$slideDown 200ms ease-out',
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: 4,
      '&:hover': {
        backgroundColor: theme.palette.grey[500],
      },
    },
  },
  dropdownList: {
    maxHeight: 360,
    overflowY: 'auto',
    padding: theme.spacing(0.5),
  },
  listItem: {
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.25, 0.5),
    padding: theme.spacing(2, 2.5),
    minHeight: 56,
    backgroundColor: 'white',
    border: '1px solid #f0f0f0',
    transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    '&:active': {
      backgroundColor: theme.palette.action.selected,
      transform: 'translateY(0px)',
    },
    '&:focus': {
      backgroundColor: theme.palette.action.hover,
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      '& .MuiListItemText-primary': {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  listItemText: {
    fontSize: '1.125rem',
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.text.primary,
    flex: 1,
    lineHeight: 1.5,
  },
  listItemSecondary: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  noResults: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    fontStyle: 'italic',
    lineHeight: 1.5,
  },
  // Animation keyframes
  '@keyframes slideDown': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-10px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
}));
